/**
 * Closed-loop workflow steps for one referral (TRUS-757 F5).
 *
 * Finds the TCO workflow instance linked to the referral
 * (tco_workflow_instances.service_referral_id), lists its steps, and shows each
 * `referral_status_gate` step with the referral status it waits for, whether it
 * is done / waiting / overdue, and its timeout (remind or escalate).
 *
 * When no instance is linked, "Start closed-loop workflow" calls
 * POST /api/referral-inbound/workflow/start {service_referral_id}.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Circle, Clock, Hourglass, Loader2, PlayCircle, Workflow, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  tcoServiceReferralsAPI,
  tcoWorkflowsAPI,
  type StepExecution,
  type WorkflowInstance,
} from '@/services/api/tcoServiceReferrals';
import { referralInboundAPI } from '@/services/api/referralInbound';
import { evaluateSteps, formatTimeout, type EvaluatedStep, type GateState } from './inboundLogic';
import { formatWhen, referralStatusLabel, type TFn } from './labels';
import { primaryBtn } from './InboundActionDialogs';

const STATE_CLASS: Record<GateState, string> = {
  done: 'text-success-ink',
  waiting: 'text-v2accent-ink',
  overdue: 'text-danger-ink',
  upcoming: 'text-ink-400',
  skipped: 'text-ink-400',
  failed: 'text-danger-ink',
};

function StateIcon({ state }: { state: GateState }) {
  const cls = `h-4 w-4 flex-shrink-0 ${STATE_CLASS[state]}`;
  switch (state) {
    case 'done': return <CheckCircle2 className={cls} aria-hidden />;
    case 'waiting': return <Hourglass className={cls} aria-hidden />;
    case 'overdue': return <AlertTriangle className={cls} aria-hidden />;
    case 'failed': return <XCircle className={cls} aria-hidden />;
    default: return <Circle className={cls} aria-hidden />;
  }
}

export function gateStateLabel(t: TFn, state: GateState): string {
  switch (state) {
    case 'done': return t('referralInbound.gates.state.done', 'Done');
    case 'waiting': return t('referralInbound.gates.state.waiting', 'Waiting');
    case 'overdue': return t('referralInbound.gates.state.overdue', 'Overdue');
    case 'skipped': return t('referralInbound.gates.state.skipped', 'Skipped');
    case 'failed': return t('referralInbound.gates.state.failed', 'Failed');
    default: return t('referralInbound.gates.state.upcoming', 'Not started');
  }
}

function onTimeoutLabel(t: TFn, action: string | undefined): string {
  if (action === 'escalate') return t('referralInbound.gates.onTimeout.escalate', 'then a staff task is created');
  return t('referralInbound.gates.onTimeout.remind', 'then the provider is reminded');
}

function StepRow({ ev }: { ev: EvaluatedStep }) {
  const { t } = useAppTranslation();
  const { step, gate, state, dueAt } = ev;
  return (
    <li className="flex gap-3 py-2" data-testid={`gate-step-${step.step_number}`}>
      <StateIcon state={state} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2">
          <span className="text-[13px] font-medium text-ink">
            {step.step_name || t('referralInbound.gates.stepN', 'Step {{n}}', { n: step.step_number })}
          </span>
          <span className={`text-[11px] font-medium ${STATE_CLASS[state]}`}>{gateStateLabel(t, state)}</span>
        </div>
        {gate?.status_at_least && (
          <div className="text-[12px] text-ink-500">
            {t('referralInbound.gates.waitsFor', 'Waits for the referral to reach {{status}}', {
              status: referralStatusLabel(t, gate.status_at_least),
            })}
          </div>
        )}
        {gate?.timeout_hours && state !== 'done' && state !== 'skipped' && (
          <div className={`flex items-center gap-1 text-[12px] ${state === 'overdue' ? 'text-danger-ink' : 'text-ink-500'}`}>
            <Clock className="h-3 w-3" aria-hidden />
            {dueAt
              ? state === 'overdue'
                ? t('referralInbound.gates.overdueSince', 'Was due {{when}}, {{action}}', { when: formatWhen(dueAt.toISOString()), action: onTimeoutLabel(t, gate.on_timeout) })
                : t('referralInbound.gates.dueBy', 'Due {{when}}, {{action}}', { when: formatWhen(dueAt.toISOString()), action: onTimeoutLabel(t, gate.on_timeout) })
              : t('referralInbound.gates.timeout', 'Allows {{timeout}}, {{action}}', { timeout: formatTimeout(gate.timeout_hours), action: onTimeoutLabel(t, gate.on_timeout) })}
          </div>
        )}
        {state === 'done' && step.completed_at && (
          <div className="text-[12px] text-ink-400">{formatWhen(step.completed_at)}</div>
        )}
      </div>
    </li>
  );
}

/**
 * The steps of one instance, with gate status. Reused by MemberWorkflowsTab.
 */
export function WorkflowGateSteps({
  instanceId,
  instanceStatus,
  referralStatus,
}: {
  instanceId: number;
  instanceStatus?: string | null;
  referralStatus?: string | null;
}) {
  const { t } = useAppTranslation();
  const [steps, setSteps] = useState<StepExecution[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSteps(null);
    setError(null);
    tcoWorkflowsAPI
      .listStepExecutions(instanceId)
      .then((s) => { if (!cancelled) setSteps(Array.isArray(s) ? s : []); })
      .catch(() => { if (!cancelled) setError(t('referralInbound.gates.stepsFailed', 'Could not load the workflow steps.')); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  if (error) return <p className="text-[12px] text-danger-ink">{error}</p>;
  if (steps === null) {
    return (
      <div className="flex items-center gap-2 py-2 text-[12px] text-ink-400">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {t('referralInbound.gates.loadingSteps', 'Loading steps…')}
      </div>
    );
  }
  if (steps.length === 0) {
    return <p className="text-[12px] text-ink-500">{t('referralInbound.gates.noSteps', 'This workflow has not started its steps yet.')}</p>;
  }
  const evaluated = evaluateSteps(steps, { referralStatus, instanceStatus });
  return (
    <ol className="divide-y divide-v2border-muted">
      {evaluated.map((ev) => <StepRow key={ev.step.step_number} ev={ev} />)}
    </ol>
  );
}

/** Pick the instance to show: the newest one that is not cancelled, else the newest. */
export function pickInstance(instances: WorkflowInstance[], referralId: number): WorkflowInstance | null {
  const linked = instances
    .filter((i) => Number(i.service_referral_id) === referralId)
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  return linked.find((i) => i.status !== 'cancelled') ?? linked[0] ?? null;
}

export default function ReferralWorkflowGates({ referralId }: { referralId: number }) {
  const { t } = useAppTranslation();
  const [instance, setInstance] = useState<WorkflowInstance | null>(null);
  const [referralStatus, setReferralStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const referral = await tcoServiceReferralsAPI.get(referralId);
      setReferralStatus(referral?.status ?? null);
      const instances = referral?.member_id
        ? await tcoWorkflowsAPI.listInstances({ member_id: referral.member_id, include_completed: true, limit: 200 })
        : [];
      setInstance(pickInstance(Array.isArray(instances) ? instances : [], referralId));
    } catch {
      setError(t('referralInbound.gates.loadFailed', 'Could not load the workflow for this referral.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralId]);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      await referralInboundAPI.startWorkflow(referralId);
      toast.success(t('referralInbound.gates.started', 'Closed-loop workflow started.'));
      await load();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : t('referralInbound.gates.startFailed', 'Could not start the workflow.'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <section className="rounded-lg border border-v2border bg-white p-4" aria-labelledby={`rwg-title-${referralId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Workflow className="h-4 w-4 text-ink-500" aria-hidden />
        <h3 id={`rwg-title-${referralId}`} className="text-[14px] font-semibold text-ink">
          {t('referralInbound.gates.title', 'Closed-loop workflow')}
        </h3>
        {instance && (
          <span className="text-[12px] text-ink-500">
            {instance.template_name || t('referralInbound.gates.instanceN', 'Workflow #{{id}}', { id: instance.id })}
          </span>
        )}
      </div>

      <div className="mt-2">
        {loading ? (
          <div className="flex items-center gap-2 py-2 text-[12px] text-ink-400">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t('referralInbound.gates.loading', 'Loading workflow…')}
          </div>
        ) : error ? (
          <p className="text-[12px] text-danger-ink">{error}</p>
        ) : instance ? (
          <WorkflowGateSteps instanceId={instance.id} instanceStatus={instance.status} referralStatus={referralStatus} />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[12px] text-ink-500">
              {t('referralInbound.gates.none', 'No workflow is tracking this referral. Start one to get reminders when the provider goes quiet.')}
            </p>
            <button type="button" className={primaryBtn} onClick={start} disabled={starting}>
              {starting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <PlayCircle className="h-4 w-4" aria-hidden />}
              {t('referralInbound.gates.start', 'Start closed-loop workflow')}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
